import { ApiClient, type ApiClientOptions } from './client';
import { agenciesApi, authApi } from './core-endpoints';
import { lazyApi } from './lazy-api';
import { brandingApi, sessionApi } from './session';
import type { aiApi } from './ai';
import type { automationsApi } from './automations';
import type { billingApi, whiteLabelApi } from './billing';
import type { bookingApi, calendarApi } from './booking';
import type { campaignsApi } from './campaigns';
import type { contactsApi } from './contacts';
import type { inboxApi } from './inbox';
import type { paymentsApi } from './payments';
import type { pipelinesApi } from './pipelines';
import type { invitesApi, securityApi } from './security';

type Table<F extends (client: ApiClient) => unknown> = ReturnType<F>;

/**
 * The app's API. Sign-in, agencies, the brand and the session's plan are built now; every
 * module's endpoint table loads with its first call (lazy-api.ts, docs/ux/plan.md B5), so
 * the first bundle carries none of them. Newer modules build theirs with moduleApi inside
 * their own chunks instead of appearing here.
 */
export function createApi(options: ApiClientOptions) {
  const client = new ApiClient(options);
  return {
    client,
    auth: authApi(client),
    agencies: agenciesApi(client),
    branding: brandingApi(client),
    session: sessionApi(client),
    calendar: lazyApi<Table<typeof calendarApi>>(client, () => import('./booking').then((m) => m.calendarApi)),
    booking: lazyApi<Table<typeof bookingApi>>(client, () => import('./booking').then((m) => m.bookingApi)),
    security: lazyApi<Table<typeof securityApi>>(client, () => import('./security').then((m) => m.securityApi)),
    invites: lazyApi<Table<typeof invitesApi>>(client, () => import('./security').then((m) => m.invitesApi)),
    pipelines: lazyApi<Table<typeof pipelinesApi>>(client, () => import('./pipelines').then((m) => m.pipelinesApi)),
    contacts: lazyApi<Table<typeof contactsApi>>(client, () => import('./contacts').then((m) => m.contactsApi)),
    payments: lazyApi<Table<typeof paymentsApi>>(client, () => import('./payments').then((m) => m.paymentsApi)),
    inbox: lazyApi<Table<typeof inboxApi>>(client, () => import('./inbox').then((m) => m.inboxApi)),
    ai: lazyApi<Table<typeof aiApi>>(client, () => import('./ai').then((m) => m.aiApi)),
    campaigns: lazyApi<Table<typeof campaignsApi>>(client, () => import('./campaigns').then((m) => m.campaignsApi)),
    automations: lazyApi<Table<typeof automationsApi>>(client, () => import('./automations').then((m) => m.automationsApi)),
    whiteLabel: lazyApi<Table<typeof whiteLabelApi>>(client, () => import('./billing').then((m) => m.whiteLabelApi)),
    billing: lazyApi<Table<typeof billingApi>>(client, () => import('./billing').then((m) => m.billingApi)),
  };
}
export type Api = ReturnType<typeof createApi>;

export { ApiClient, decodeAccessClaims, type ApiClientOptions, type ClientState, type FetchLike, type RefreshTransport } from './client';
export { ApiError, errorMessage, isApiError } from './errors';
export { HOME_SCOPE, scopeKey, type SessionScope } from './scope';
export { memorySessionStore, webSessionStore, type SessionStore, type StoredSession } from './session-store';
export type { LoginResult } from './core-endpoints';
export type * from './inbox';
export type * from './automations';
export type * from './ai';
export type * from './campaigns';
export type * from './types';
export type * from './contacts';
export { CONSENT_CHANNELS } from './contact-consent';
export { preloadApi } from './lazy-api';
